import log from "loglevel";

import { type Token } from "./definitions";
import { api } from "./api";
import { storeData, getStoredData } from "./localStorage";
import { clearStoredParticipantNames } from "./participantStorage";

const LOCAL_STORAGE_ITEM_ID = "TWILIO_WEBCHAT_WIDGET";

export const sessionDataHandler = {
    tryResumeExistingSession(): Token | null {
        log.debug("sessionDataHandler: trying to refresh existing session");
        const storedTokenData = getStoredData<Token>(LOCAL_STORAGE_ITEM_ID);

        if (!storedTokenData) {
            log.debug("sessionDataHandler: no tokens stored, no session to refresh");
            return null;
        }

        if (Date.now() >= storedTokenData.expiration) {
            log.debug("sessionDataHandler: token expired, ignoring existing sessions");
            return null;
        }

        log.debug("sessionDataHandler: existing token still valid, using existing session data");
        return storedTokenData;
    },

    async getUpdatedToken(): Promise<Token> {
        log.debug("sessionDataHandler: trying to get updated token from BE");
        const storedTokenData = getStoredData<Token>(LOCAL_STORAGE_ITEM_ID);

        if (!storedTokenData) {
            throw Error("Can't update token: current token doesn't exist");
        }

        let newTokenData: Token;

        try {
            newTokenData = await api.contactBackend<Token>("/refreshToken", {
                token: storedTokenData.token
            });
        } catch (e) {
            throw Error(`Something went wrong when trying to get an updated token: ${e}`);
        }

        newTokenData.expiration = Date.now() + Number(newTokenData.expiration) * 1000;

        // Server won't return a conversation SID, so we merge the existing data with the latest one
        const updatedSessionData = {
            ...storedTokenData,
            ...newTokenData
        };

        storeData<Token>(LOCAL_STORAGE_ITEM_ID, updatedSessionData);

        return updatedSessionData;
    },

    fetchAndStoreNewSession: async (payload: Record<string, unknown>) => {
        log.debug("sessionDataHandler: trying to create new session");

        let newTokenData;

        try {
            newTokenData = await api.contactBackend<Token>("/init", payload);
        } catch (e) {
            log.error("sessionDataHandler: error fetching new token", e);
            throw Error("No results from server");
        }

        log.debug("sessionDataHandler: new session successfully created");
        newTokenData.expiration = Date.now() + Number(newTokenData.expiration) * 1000;
        clearStoredParticipantNames();
        storeData<Token>(LOCAL_STORAGE_ITEM_ID, newTokenData);

        return newTokenData;
    },

    clear: () => {
        localStorage.removeItem(LOCAL_STORAGE_ITEM_ID);
        clearStoredParticipantNames();
    }
};
